import useTranslate from "hooks/useTranslate";
import useTraitSales from "hooks/useTraitSales";
import useAssets from "hooks/useAssets";
import { useMemo } from "react";
import { BigNumber } from "ethers";
import { Button } from "@chakra-ui/react";
import usePurchaseTraitsUsingEth from "hooks/usePurchaseTraitsUsingEth";
import { formatEtherBalance } from "utils/numberUtils";

type EthPurchaseContainerProps = {
  tokenId: number;
  traitsToBuy: number[];
  onSuccess: () => void;
};

const EthPurchaseContainer = ({
  tokenId,
  traitsToBuy,
  onSuccess,
}: EthPurchaseContainerProps) => {
  const translate = useTranslate();
  const { traitSalesByTokenId } = useTraitSales();
  const { balance } = useAssets();

  const totalPrice = useMemo(
    () =>
      traitsToBuy.reduce(
        (prev: BigNumber, tokenId) =>
          prev.add(traitSalesByTokenId?.[tokenId].price || "0"),
        BigNumber.from("0")
      ),
    [traitsToBuy, traitSalesByTokenId]
  );

  const purchaseData = useMemo(
    () => ({
      tokenId,
      ethAmount: totalPrice,
      traitTokenIds: traitsToBuy,
      amountPerTrait: traitsToBuy.map(() => 1),
    }),
    [tokenId, totalPrice, traitsToBuy]
  );

  const [purchase, isPurchasing] = usePurchaseTraitsUsingEth(
    purchaseData,
    onSuccess
  );

  const isTooExpensive = balance ? totalPrice.gt(balance) : true;

  return (
    <Button
      w="full"
      colorScheme="secondary"
      onClick={() => purchase()}
      isDisabled={isTooExpensive}
      isLoading={isPurchasing}
    >
      {translate("purchase:submit", {
        price: formatEtherBalance(totalPrice),
        currency: "ETH",
      })}
    </Button>
  );
};

export default EthPurchaseContainer;
